import {useCallback, useState} from 'react'
import {Grid, Box, Tabs, AnimationProvider, Animate, Text} from '@primer/react-brand/esm'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'

import {AssetTypeEnum} from '../../types'
import {AssetVideo} from '../Video'
import type {FlexSuiteCarouselHeroProps} from './types'
import {HeroContent} from './HeroContent'
import {HeroCarouselImage} from './HeroCarouselImage'
import {HeroCarouselMediaFrame} from './HeroCarouselMediaFrame'
import {getDescription} from './utils'
import styles from './FlexSuiteHeroCarousel.module.css'

/**
 * Carousel hero: center-aligned hero with tabbed image/video carousel below
 */
export function FlexSuiteHeroCarousel({fields}: {fields: FlexSuiteCarouselHeroProps}) {
  const {heroCarousel} = fields
  const disableAnimations = isFeatureEnabled('site_landing_pages_disable_hero_animations')

  const [activeTab, setActiveTab] = useState<string>('0')
  const handleTabChange = useCallback((id: string) => {
    // Deferred because Primer Brand's Tabs calls onChange during its render cycle,
    // which would otherwise trigger a React "setState during render" warning.
    queueMicrotask(() => setActiveTab(id))
  }, [])

  return (
    // The block-end border doubles as the visual separator to whatever section follows.
    // Used by both Overview and Details templates; verify visually in the Overview Storybook
    // story after touching this wrapper.
    <Box data-testid="hero-carousel" borderBlockEndWidth="thin" borderStyle="solid" borderColor="muted">
      <HeroContent {...fields} />
      {heroCarousel && heroCarousel.length > 0 && (
        <Box borderBlockStartWidth="thin" borderStyle="solid" borderColor="muted">
          <Grid style={{paddingInline: 0}}>
            <Grid.Column>
              <Box
                borderColor="muted"
                borderInlineStartWidth={{wide: 'thin'}}
                borderInlineEndWidth={{wide: 'thin'}}
                borderStyle="solid"
              >
                <AnimationProvider
                  disableAnimations={disableAnimations}
                  autoStaggerChildren={false}
                  animationTrigger="immediate"
                  runOnce
                >
                  {heroCarousel.map((item, index) => {
                    const {assetType, optionVideo, optionImage, optionPoster, autoplay, videoSource} = item.fields

                    return (
                      <Box
                        key={item.sys.id}
                        id={`carousel-panel-image-${index}`}
                        aria-labelledby={`carousel-tab-${index}`}
                        role="tabpanel"
                        tabIndex={0}
                        hidden={activeTab !== String(index)}
                      >
                        {assetType === AssetTypeEnum.enum.Image ? (
                          optionImage && (
                            <HeroCarouselImage
                              image={optionImage}
                              position={item.fields.position ?? 'default'}
                              isActive={activeTab === String(index)}
                            />
                          )
                        ) : assetType === AssetTypeEnum.enum['Uploaded Video'] ? (
                          <HeroCarouselMediaFrame isActive={activeTab === String(index)}>
                            <AssetVideo
                              assetType={assetType}
                              optionVideo={optionVideo}
                              optionPoster={optionPoster}
                              autoplay={autoplay}
                            />
                          </HeroCarouselMediaFrame>
                        ) : (
                          <Box className={styles.VideoFrame}>
                            <Animate animate="slide-in-up">
                              <AssetVideo
                                assetType={assetType}
                                optionVideo={optionVideo}
                                optionPoster={optionPoster}
                                autoplay={autoplay}
                                videoSource={videoSource}
                              />
                            </Animate>
                          </Box>
                        )}
                      </Box>
                    )
                  })}
                </AnimationProvider>
              </Box>
              <Box
                paddingBlockStart={{narrow: 'condensed', regular: 32}}
                paddingBlockEnd={{narrow: 'condensed', regular: 32}}
                paddingInlineStart={{narrow: 'condensed', regular: 48}}
                paddingInlineEnd={{narrow: 'condensed', regular: 48}}
                borderInlineStartWidth={{wide: 'thin'}}
                borderInlineEndWidth={{wide: 'thin'}}
                borderStyle="solid"
                borderColor="muted"
              >
                <Tabs variant="default" onChange={handleTabChange} aria-label="Carousel">
                  {heroCarousel.map((item, index) => (
                    <Tabs.Item
                      key={item.sys.id}
                      id={`carousel-tab-${index}`}
                      aria-controls={`carousel-panel-image-${index} carousel-panel-text-${index}`}
                    >
                      {documentToPlainTextString(item.fields.heading)}
                    </Tabs.Item>
                  ))}
                </Tabs>
                <AnimationProvider
                  disableAnimations={disableAnimations}
                  autoStaggerChildren={false}
                  animationTrigger="immediate"
                  runOnce
                >
                  <Box padding="condensed">
                    {heroCarousel.map((item, index) => (
                      <Box
                        key={item.sys.id}
                        id={`carousel-panel-text-${index}`}
                        aria-labelledby={`carousel-tab-${index}`}
                        role="tabpanel"
                        tabIndex={0}
                        hidden={activeTab !== String(index)}
                      >
                        <Animate animate="slide-in-up">
                          <Text as="p" align="center">
                            {getDescription(item.fields.description)}
                          </Text>
                        </Animate>
                      </Box>
                    ))}
                  </Box>
                </AnimationProvider>
              </Box>
            </Grid.Column>
          </Grid>
        </Box>
      )}
    </Box>
  )
}
